import type { MinimalUserDto } from '@issuemanager/api-client';
import {
  ContainerPopper,
  FakeDropdownBox,
  UserMultipleSelector,
} from '@issuemanager/apps/client/components';
import { DISPLAY_NAME_LIMIT } from '@issuemanager/apps/client/utils';
import { Stack } from '@mui/material';
import { ReactNode, useMemo, type JSX } from 'react';
import type { UserMultipleSelectorProps } from '../user-multiple-selector';

export interface UserMultipleSelectorFieldProps<
  T extends MinimalUserDto = MinimalUserDto,
> extends Pick<
    UserMultipleSelectorProps<T>,
    'issueTypeId' | 'issueSubtypeId' | 'getTestIdOption'
  > {
  value?: T[];
  onChange: (value: T[]) => void;
  label?: ReactNode;
  readOnly?: boolean;
  visible?: boolean;
  placeholder?: string;
  'data-testid'?: string;
}

export function UserMultipleSelectorField<
  T extends MinimalUserDto = MinimalUserDto,
>({
  value = [],
  onChange,
  label,
  readOnly,
  visible = true,
  placeholder,
  'data-testid': dataTestId,
  ...selectorProps
}: UserMultipleSelectorFieldProps<T>): JSX.Element | null {
  const displayText = useMemo(
    () =>
      value.length > 0
        ? `(${value.length}) ${value
            .slice(0, DISPLAY_NAME_LIMIT)
            .map((u) => u.name)
            .join(', ')}`
        : null,
    [value],
  );

  if (!visible) return null;

  return (
    <Stack>
      {label ?? null}
      <ContainerPopper
        renderTarget={({ handleClick, handleKeyDown, open }) => (
          <FakeDropdownBox
            onClick={handleClick}
            onKeyDown={handleKeyDown}
            tabIndex={0}
            role='button'
            aria-haspopup='listbox'
            aria-expanded={open}
            readOnly={readOnly}
            placeholder={placeholder}
            data-testid={dataTestId}
          >
            {displayText}
          </FakeDropdownBox>
        )}
        renderChildren={({ handleClose }) => (
          <UserMultipleSelector<T>
            open
            value={value}
            onChange={onChange}
            onClose={handleClose}
            TextFieldProps={{ autoFocus: true }}
            {...selectorProps}
          />
        )}
      />
    </Stack>
  );
}
